/**
 * HOOK: useServerPagination
 * Hook wrapper pour gérer la pagination server-side avec debounce automatique
 *
 * Gère automatiquement :
 * - Le debounce de la recherche (300ms)
 * - Le reset de page lors du changement de recherche/filtres
 * - Le loading state
 * - Les métadonnées de pagination
 *
 * @example
 * const { data, loading, total, setSearchTerm, setCurrentPage } = useServerPagination(
 *   contacts.fetchPaginated,
 *   10 // items per page
 * );
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../components/ui';
import type { PaginatedResult, QueryParams } from '../types/crud';
import type { FilterCondition, FilterMode } from '../types/filters';
import { useDebounce } from './useDebounce';

export interface UseServerPaginationOptions {
  /**
   * Nombre d'éléments par page initial
   * @default 10
   */
  initialLimit?: number;

  /**
   * Délai de debounce pour la recherche (en ms)
   * @default 300
   */
  debounceDelay?: number;

  /**
   * Filtres initiaux à appliquer au premier fetch
   */
  initialFilters?: FilterCondition[];

  /**
   * Mode de filtre initial
   * @default 'and'
   */
  initialFilterMode?: FilterMode;

  /**
   * Tri initial
   */
  initialSort?: {
    field: string;
    direction: 'asc' | 'desc';
  };
}

export interface UseServerPaginationReturn<T> {
  // Données
  data: T[];
  loading: boolean;
  total: number;
  totalPages: number;

  // Pagination
  currentPage: number;
  itemsPerPage: number;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (items: number) => void;

  // Recherche
  searchTerm: string;
  setSearchTerm: (term: string) => void;

  // Filtres
  filters: FilterCondition[];
  filterMode: FilterMode;
  setFilters: (filters: FilterCondition[], mode?: FilterMode) => void;
  requiredFilters: FilterCondition[];
  setRequiredFilters: (filters: FilterCondition[]) => void;

  // Tri
  sortField: string | null;
  sortDirection: 'asc' | 'desc';
  setSort: (field: string | null, direction?: 'asc' | 'desc') => void;

  // Actions
  refresh: () => Promise<void>;
}

export function useServerPagination<T>(
  fetchPaginated: (params: QueryParams) => Promise<PaginatedResult<T>>,
  options: UseServerPaginationOptions = {}
): UseServerPaginationReturn<T> {
  const {
    initialLimit = 10,
    debounceDelay = 300,
    initialFilters = [],
    initialFilterMode = 'and',
    initialSort,
  } = options;

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<FilterCondition[]>(initialFilters);
  const [filterMode, setFilterMode] = useState<FilterMode>(initialFilterMode);
  const [requiredFilters, setRequiredFilters] = useState<FilterCondition[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState<string | null>(initialSort?.field || null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    initialSort?.direction || 'desc'
  );

  // Debounce la recherche, les filtres et le mode pour éviter trop de requêtes
  const debouncedSearch = useDebounce(searchTerm, debounceDelay);
  const debouncedFilters = useDebounce(filters, debounceDelay);
  const debouncedFilterMode = useDebounce(filterMode, debounceDelay);
  const debouncedRequiredFilters = useDebounce(requiredFilters, debounceDelay);

  // Garder une référence stable de fetchPaginated
  const fetchPaginatedRef = useRef(fetchPaginated);

  useEffect(() => {
    fetchPaginatedRef.current = fetchPaginated;
  }, [fetchPaginated]);

  // Seule la dernière requête lancée écrit l'état : sinon la liste non filtrée
  // du montage, plus lente, écrase celle des filtres restaurés (issue #293).
  const latestRequestRef = useRef(0);

  // Paramètres courants, lus par refresh() : une callback capturée avant un
  // changement de filtre ou de page ne doit pas recharger l'ancien périmètre.
  const paramsRef = useRef<QueryParams>({});
  paramsRef.current = {
    page: currentPage,
    limit: itemsPerPage,
    search: debouncedSearch,
    filters: debouncedFilters,
    filterMode: debouncedFilterMode,
    requiredFilters: debouncedRequiredFilters,
    sort: sortField ? { field: sortField, direction: sortDirection } : undefined,
  };

  // Ref pour tracker si c'est le premier render (éviter reset page au montage)
  const isFirstRender = useRef(true);

  // Refs pour tracker les changements de recherche/filtres
  const prevDebouncedSearch = useRef(debouncedSearch);
  const prevDebouncedFilters = useRef(debouncedFilters);

  /**
   * Reset la page à 1 quand la recherche ou les filtres changent (après debounce)
   * Cela évite les fetches multiples à chaque frappe
   */
  useEffect(() => {
    // Skip le premier render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Vérifier si la recherche ou les filtres ont changé
    const searchChanged = prevDebouncedSearch.current !== debouncedSearch;
    const filtersChanged =
      JSON.stringify(prevDebouncedFilters.current) !== JSON.stringify(debouncedFilters);

    // Mettre à jour les refs
    prevDebouncedSearch.current = debouncedSearch;
    prevDebouncedFilters.current = debouncedFilters;

    // Reset à la page 1 si la recherche ou les filtres ont changé
    if ((searchChanged || filtersChanged) && currentPage !== 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, debouncedFilters]);

  /**
   * Refetch quand les paramètres changent
   * On ne crée pas de fonction fetch memoïsée, on fetch directement dans useEffect
   */
  useEffect(() => {
    const fetchData = async () => {
      const requestId = ++latestRequestRef.current;
      setLoading(true);
      try {
        const result = await fetchPaginatedRef.current({
          page: currentPage,
          limit: itemsPerPage,
          search: debouncedSearch,
          filters: debouncedFilters,
          filterMode: debouncedFilterMode,
          requiredFilters: debouncedRequiredFilters,
          sort: sortField ? { field: sortField, direction: sortDirection } : undefined,
        });
        if (requestId !== latestRequestRef.current) return;

        setData(result.data);
        setTotal(result.total);
        setTotalPages(result.totalPages);
      } catch (error) {
        if (requestId !== latestRequestRef.current) return;
        console.error('[useServerPagination] Error fetching data:', error);
        // Sans ce retour visible, un echec de fetch se traduit par un simple
        // « Aucune donnee a afficher » indiscernable d'une liste vide (issue #230).
        // Ne couvre que les fetchPaginated qui REJETTENT (crews, contacts) ;
        // useProspects/useCRUD avalent l'erreur en amont et renvoient une page vide.
        // `id` fixe : l'effet se redeclenche a chaque changement de dependances,
        // sinon les toasts s'empilent.
        toast.error('Impossible de charger la liste. Réessayez ou réinitialisez vos filtres.', {
          id: 'server-pagination-error',
        });
        setData([]);
        setTotal(0);
        setTotalPages(0);
      } finally {
        if (requestId === latestRequestRef.current) setLoading(false);
      }
    };

    fetchData();
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearch,
    debouncedFilters,
    debouncedFilterMode,
    debouncedRequiredFilters,
    sortField,
    sortDirection,
  ]);

  /**
   * Handler pour changer le terme de recherche
   * Le reset de page sera fait après le debounce via l'effet dédié
   */
  const handleSetSearchTerm = useCallback((term: string) => {
    setSearchTerm(term);
    // ✅ NE PAS reset la page ici - sera fait après le debounce
    // pour éviter les fetches multiples à chaque frappe
  }, []);

  /**
   * Handler pour changer le nombre d'items par page
   * Reset automatiquement à la page 1
   */
  const handleSetItemsPerPage = useCallback((items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1); // Reset page quand items per page change
  }, []);

  /**
   * Handler pour changer les filtres
   * Le reset de page sera fait après le debounce via l'effet dédié
   * ✅ Compare les filtres avant de mettre à jour pour éviter les re-renders inutiles
   */
  const handleSetFilters = useCallback((newFilters: FilterCondition[], newMode?: FilterMode) => {
    // Mettre à jour le mode si fourni
    if (newMode !== undefined) {
      setFilterMode(newMode);
    }

    setFilters((currentFilters) => {
      // Comparer les filtres par contenu, pas par référence
      const areFiltersEqual =
        currentFilters.length === newFilters.length &&
        currentFilters.every((filter, index) => {
          const newFilter = newFilters[index];
          return (
            filter.field === newFilter.field &&
            filter.operator === newFilter.operator &&
            JSON.stringify(filter.value) === JSON.stringify(newFilter.value)
          );
        });

      if (areFiltersEqual && newMode === undefined) {
        return currentFilters; // Retourner la même référence pour éviter le re-render
      }

      // ✅ NE PAS reset la page ici - sera fait après le debounce
      // pour éviter les fetches multiples
      return newFilters;
    });
  }, []);

  /**
   * Handler pour changer les filtres obligatoires
   * Reset automatiquement à la page 1
   */
  const handleSetRequiredFilters = useCallback((newFilters: FilterCondition[]) => {
    setRequiredFilters((currentFilters) => {
      const areFiltersEqual =
        currentFilters.length === newFilters.length &&
        currentFilters.every((filter, index) => {
          const newFilter = newFilters[index];
          return (
            filter.field === newFilter.field &&
            filter.operator === newFilter.operator &&
            JSON.stringify(filter.value) === JSON.stringify(newFilter.value)
          );
        });

      if (areFiltersEqual) {
        return currentFilters;
      }

      setCurrentPage(1);
      return newFilters;
    });
  }, []);

  /**
   * Handler pour changer le tri
   * Reset automatiquement à la page 1
   */
  const handleSetSort = useCallback((field: string | null, direction?: 'asc' | 'desc') => {
    // Ignorer les identifiants réservés de TanStack Table (colonnes non-données)
    const RESERVED_COLUMN_IDS = new Set(['select', 'actions', 'expand']);
    if (field && RESERVED_COLUMN_IDS.has(field)) return;
    setSortField(field);
    if (direction) {
      setSortDirection(direction);
    }
    setCurrentPage(1); // Reset page on sort change
  }, []);

  /**
   * Forcer un refresh manuel
   * Utile après création/suppression d'items
   */
  const handleRefresh = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    try {
      const result = await fetchPaginatedRef.current(paramsRef.current);
      if (requestId !== latestRequestRef.current) return;

      setData(result.data);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error('[useServerPagination] Error refreshing data:', error);
      // Meme raison que dans l'effet principal : sans toast, le refresh manuel
      // echoue en silence en laissant les donnees perimees a l'ecran.
      toast.error('Impossible de rafraîchir la liste. Réessayez.', {
        id: 'server-pagination-error',
      });
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  }, []);

  return {
    // Données
    data,
    loading,
    total,
    totalPages,

    // Pagination
    currentPage,
    itemsPerPage,
    setCurrentPage,
    setItemsPerPage: handleSetItemsPerPage,

    // Recherche
    searchTerm,
    setSearchTerm: handleSetSearchTerm,

    // Filtres
    filters,
    filterMode,
    setFilters: handleSetFilters,
    requiredFilters,
    setRequiredFilters: handleSetRequiredFilters,

    // Tri
    sortField,
    sortDirection,
    setSort: handleSetSort,

    // Actions
    refresh: handleRefresh,
  };
}
